import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';

function fmtDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Stars({ rating }) {
  const r = Math.max(0, Math.min(5, rating || 0));
  return (
    <span style={{ color: '#F59E0B', letterSpacing: 1 }}>
      {'\u2605'.repeat(r)}{'\u2606'.repeat(5 - r)}
    </span>
  );
}

function Card({ children, style }) {
  return <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, ...style }}>{children}</div>;
}

const smallBtn = { background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0.3rem 0.8rem', fontSize: '0.78rem', cursor: 'pointer', color: 'var(--mid)' };
const brandBtn = { background: 'var(--brand, #E85D04)', color: 'white', border: 'none', borderRadius: 6, padding: '0.4rem 0.9rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' };

export default function GoogleReviews() {
  const { restaurant } = useAuth();
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [generatingIds, setGeneratingIds] = useState(new Set());
  const [copiedIds, setCopiedIds]         = useState(new Set());
  const [editTexts, setEditTexts]         = useState({});
  const [savingIds, setSavingIds]         = useState(new Set());
  const [postingIds, setPostingIds]       = useState(new Set());
  const [syncing, setSyncing]             = useState(false);
  const [connecting, setConnecting]       = useState(false);
  const [notice, setNotice]               = useState('');

  const toggleId = (setter, id, on) => setter(prev => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const load = async (silent) => {
    if (!restaurant?.id) return;
    if (!silent) setLoading(true);
    const result = await portalFetch('/google-reviews');
    if (result) setData(result);
    setLoading(false);
  };

  useEffect(() => { load(false); }, [restaurant?.id]);

  // Result of the Google sign-in round trip (?google=connected, etc.)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const g = p.get('google');
    if (!g) return;
    const messages = {
      connected: 'Google connected. Your reviews are syncing.',
      denied: 'Google sign-in was cancelled. Nothing was connected.',
      needs_location: 'Google is connected, but we could not tell which Google location is yours. Please contact TableChat support.',
      error: 'Something went wrong connecting Google. Please try again.',
    };
    setNotice(messages[g] || messages.error);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const updateReview = (id, patch) => {
    setData(prev => prev ? { ...prev, reviews: prev.reviews.map(r => r.id === id ? { ...r, ...patch } : r) } : prev);
  };

  const clearEdit = (id) => setEditTexts(prev => { const n = { ...prev }; delete n[id]; return n; });

  const handleGenerate = async (id) => {
    toggleId(setGeneratingIds, id, true);
    const result = await portalFetch('/generate-google-response', {
      method: 'POST',
      body: JSON.stringify({ reviewId: id }),
    });
    if (result?.draftResponse) {
      updateReview(id, { draft_response: result.draftResponse, sentiment: result.sentiment, severity: result.severity });
      clearEdit(id);
    } else {
      // Refused (for example a critical review) or failed: reload to pick up the saved severity.
      await load(true);
    }
    toggleId(setGeneratingIds, id, false);
  };

  const handleSave = async (id) => {
    const text = editTexts[id];
    if (text === undefined) return;
    toggleId(setSavingIds, id, true);
    const result = await portalFetch('/save-response', {
      method: 'POST',
      body: JSON.stringify({ reviewId: id, source: 'google', draftResponse: text }),
    });
    if (typeof result?.draftResponse === 'string') {
      updateReview(id, { draft_response: result.draftResponse });
      clearEdit(id);
    }
    toggleId(setSavingIds, id, false);
  };

  const handleConnect = async () => {
    if (restaurant?.role === 'reviews') { setNotice('Only the restaurant owner can connect Google. Please ask them to do it.'); return; }
    setConnecting(true);
    const result = await portalFetch('/google-connect');
    if (result?.url) {
      window.location.href = result.url;
      return;
    }
    setNotice('Google connection is not set up yet. Please contact TableChat support.');
    setConnecting(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    setNotice('');
    const result = await portalFetch('/google-sync', { method: 'POST' });
    if (result && typeof result.fetched === 'number') {
      setNotice('Synced ' + result.fetched + ' Google reviews.');
    } else {
      setNotice('Could not sync right now. It may have just synced, or Google access may need to be reconnected.');
    }
    await load(true);
    setSyncing(false);
  };

  const handlePost = async (id) => {
    if (!window.confirm('Post this reply publicly on Google? It will be visible to everyone.')) return;
    toggleId(setPostingIds, id, true);
    setNotice('');
    const result = await portalFetch('/post-google-reply', {
      method: 'POST',
      body: JSON.stringify({ reviewId: id }),
    });
    if (result?.replyText) {
      updateReview(id, { reply_text: result.replyText, reply_time: result.replyTime, reply_status: 'replied' });
    } else {
      setNotice('Could not post the reply. Check that Google is still connected, then try again.');
    }
    toggleId(setPostingIds, id, false);
  };

  const handleCopy = (id, text) => {
    navigator.clipboard.writeText(text);
    toggleId(setCopiedIds, id, true);
    setTimeout(() => toggleId(setCopiedIds, id, false), 1800);
  };

  const reviews = data?.reviews || [];
  const agg     = data?.aggregate || { total: 0, avgRating: null };
  const conn    = data?.connection || { connected: false };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900 }}>Google Reviews</h1>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 2 }}>
          What guests say about {restaurant?.name} on Google
        </p>
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>Loading Google reviews...</div>
      ) : (
        <>
          {notice && (
            <div style={{ fontSize: '0.85rem', color: 'var(--ink)', background: 'var(--warm)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem' }}>
              {notice}
            </div>
          )}

          <Card style={{ padding: '1rem 1.4rem', marginBottom: '1.2rem' }}>
            {conn.connected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220, fontSize: '0.85rem', color: 'var(--ink)' }}>
                  {conn.lastSyncStatus === 'reauth_needed' ? (
                    <span style={{ color: '#791F1F' }}>Google access has expired. Please reconnect to keep your reviews up to date.</span>
                  ) : conn.lastSyncStatus === 'needs_location' || !conn.hasLocation ? (
                    <span style={{ color: '#791F1F' }}>Connected, but we could not tell which Google location is yours. Please contact TableChat support.</span>
                  ) : (
                    <>
                      Google Business Profile connected.{' '}
                      <span style={{ color: 'var(--muted)' }}>
                        {conn.lastSyncedAt ? 'Last synced ' + fmtDate(conn.lastSyncedAt) + '. Reviews sync daily.' : 'Waiting for the first sync.'}
                      </span>
                    </>
                  )}
                </div>
                {conn.lastSyncStatus === 'reauth_needed' ? (
                  <button onClick={handleConnect} disabled={connecting} style={{ ...brandBtn, opacity: connecting ? 0.6 : 1 }}>
                    {connecting ? 'Opening Google...' : 'Reconnect Google'}
                  </button>
                ) : (
                  <button onClick={handleSync} disabled={syncing || !conn.hasLocation} style={{ ...smallBtn, opacity: (syncing || !conn.hasLocation) ? 0.6 : 1 }}>
                    {syncing ? 'Syncing...' : 'Sync now'}
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--ink)' }}>Connect your Google Business Profile</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 2 }}>
                    Sign in with the Google account that manages your listing. New reviews will appear here daily, with AI-drafted replies you can edit and post.
                  </div>
                </div>
                <button onClick={handleConnect} disabled={connecting} style={{ ...brandBtn, opacity: connecting ? 0.6 : 1 }}>
                  {connecting ? 'Opening Google...' : 'Connect Google'}
                </button>
              </div>
            )}
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            <Card style={{ padding: '1.2rem 1.4rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Google reviews</div>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 900, color: 'var(--orange)' }}>{agg.total}</div>
            </Card>
            <Card style={{ padding: '1.2rem 1.4rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Average rating</div>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 900, color: 'var(--orange)' }}>
                {agg.avgRating ? '\u2605' + agg.avgRating : '-'}
              </div>
            </Card>
          </div>

          {reviews.length === 0 ? (
            <Card style={{ padding: '3rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>{'\u2B50'}</div>
              <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No Google reviews imported yet.</p>
            </Card>
          ) : (
            reviews.map(review => {
              const editing = editTexts[review.id];
              const dirty   = editing !== undefined && editing !== review.draft_response;
              return (
                <Card key={review.id} style={{ padding: '1.1rem 1.4rem', marginBottom: '0.9rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    {review.reviewer_photo_url && (
                      <img src={review.reviewer_photo_url} alt="" width="28" height="28" style={{ borderRadius: '50%' }} referrerPolicy="no-referrer" />
                    )}
                    <strong style={{ fontSize: '0.9rem', color: 'var(--ink)' }}>{review.reviewer_name || 'A Google user'}</strong>
                    <Stars rating={review.rating} />
                    <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{fmtDate(review.review_time)}</span>
                    {review.review_url && (
                      <a href={review.review_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.78rem', color: 'var(--brand, #E85D04)', marginLeft: 'auto' }}>View on Google</a>
                    )}
                  </div>

                  <div style={{ fontSize: '0.875rem', color: 'var(--ink)', lineHeight: 1.5 }}>
                    {review.review_text ? review.review_text : <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Rating only, no written review.</span>}
                  </div>

                  <div style={{ marginTop: '0.7rem', paddingTop: '0.7rem', borderTop: '1px solid var(--warm)' }}>
                    {review.reply_text ? (
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>
                          Your reply on Google{review.reply_time ? ' - ' + fmtDate(review.reply_time) : ''}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--ink)', lineHeight: 1.5, background: 'var(--warm)', padding: '0.7rem 0.9rem', borderRadius: 8 }}>
                          {review.reply_text}
                        </div>
                      </div>
                    ) : review.severity === 'critical' ? (
                      <div style={{ fontSize: '0.8rem', color: '#791F1F', background: '#FCEBEB', padding: '0.5rem 0.8rem', borderRadius: 8, display: 'inline-block' }}>
                        {'\u26A0\uFE0F'} This review needs a personal response, not an AI-drafted one. Reply to it directly on Google.
                      </div>
                    ) : review.draft_response ? (
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>
                          Draft reply (editable)
                        </div>
                        <div style={{ background: 'var(--warm)', padding: '0.7rem 0.9rem', borderRadius: 8, marginBottom: '0.5rem' }}>
                          <textarea
                            value={editing ?? review.draft_response}
                            onChange={e => setEditTexts(prev => ({ ...prev, [review.id]: e.target.value }))}
                            rows={4}
                            maxLength={1000}
                            aria-label="Edit draft reply"
                            style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent', resize: 'vertical', font: 'inherit', fontSize: '0.85rem', color: 'var(--ink)', lineHeight: 1.5, outline: 'none' }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {dirty && (
                            <button onClick={() => handleSave(review.id)} disabled={savingIds.has(review.id)} style={{ ...brandBtn, padding: '0.3rem 0.8rem', fontSize: '0.78rem', opacity: savingIds.has(review.id) ? 0.6 : 1 }}>
                              {savingIds.has(review.id) ? 'Saving...' : 'Save edits'}
                            </button>
                          )}
                          {conn.connected && conn.hasLocation && (
                            <button
                              onClick={() => handlePost(review.id)}
                              disabled={dirty || postingIds.has(review.id)}
                              title={dirty ? 'Save your edits first' : ''}
                              style={{ ...brandBtn, padding: '0.3rem 0.8rem', fontSize: '0.78rem', opacity: (dirty || postingIds.has(review.id)) ? 0.5 : 1 }}
                            >
                              {postingIds.has(review.id) ? 'Posting...' : 'Post to Google'}
                            </button>
                          )}
                          <button onClick={() => handleCopy(review.id, editing ?? review.draft_response)} style={smallBtn}>
                            {copiedIds.has(review.id) ? 'Copied!' : 'Copy'}
                          </button>
                          <button onClick={() => handleGenerate(review.id)} disabled={generatingIds.has(review.id)} style={{ ...smallBtn, opacity: generatingIds.has(review.id) ? 0.6 : 1 }}>
                            {generatingIds.has(review.id) ? 'Regenerating...' : 'Regenerate'}
                          </button>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                          {conn.connected && conn.hasLocation
                            ? 'Post to Google publishes this reply publicly. You can also copy it and paste it on Google yourself.'
                            : 'Connect Google to post replies from here. Until then, copy the reply and paste it under the review on Google.'}
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => handleGenerate(review.id)} disabled={generatingIds.has(review.id)} style={{ ...brandBtn, opacity: generatingIds.has(review.id) ? 0.6 : 1 }}>
                        {generatingIds.has(review.id) ? 'Generating...' : 'Generate reply'}
                      </button>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </>
      )}
    </div>
  );
}